import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  color as d3Color,
  interpolateRgb,
  scaleSequential,
  scaleSequentialLog,
} from 'd3';
import { useRecoilValue } from 'recoil';
import { positiveTrendColorState, negativeTrendColorState } from 'states';
import { dayjs, ET, etCalendarDaysUntil } from 'util/shared/date';
import { MIN_IV, SurfaceView, VolSurfaceGrids } from './useVolSurfaceGrids';

interface VolatilityHeatmapProps {
  surfaces: VolSurfaceGrids;
  view: SurfaceView;
}

const formatIv = (v: number): string => `${(v * 100).toFixed(1)}%`;
const formatGap = (v: number): string =>
  `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
const formatStrike = (strike: number): string =>
  strike.toLocaleString('en-US', { maximumFractionDigits: 2 });
// Grid orientation is deliberately strike-rows / DTE-columns - the opposite
// of the Fixed Strike Matrix in the Vol Dashboard (expiry-rows / strike-
// columns).
const VolatilityHeatmap = ({ surfaces, view }: VolatilityHeatmapProps) => {
  const theme = useTheme();
  const posColor = useRecoilValue(positiveTrendColorState);
  const negColor = useRecoilValue(negativeTrendColorState);

  const { strikes, dtes, grid, minVal, maxVal, maxAbsGap } = useMemo(() => {
    const zValues =
      view === 'gap'
        ? surfaces.gapZValues
        : view === 'projected'
          ? surfaces.projectedZValues
          : surfaces.currentZValues;

    // zValues is [dteIdx][strikeIdx] (shared with the 3D surface trace) -
    // transpose to strike-rows / DTE-columns for this grid.
    const strikes = surfaces.strikeGrid;
    const dtes = surfaces.dteValues;
    const grid = strikes.map((_, r) =>
      dtes.map((_, c) => zValues[c]?.[r] ?? NaN),
    );

    let minVal = Infinity;
    let maxVal = -Infinity;
    let maxAbsGap = 0;
    grid.forEach((row) =>
      row.forEach((v) => {
        if (!Number.isNaN(v)) {
          minVal = Math.min(minVal, v);
          maxVal = Math.max(maxVal, v);
          maxAbsGap = Math.max(maxAbsGap, Math.abs(v));
        }
      }),
    );
    if (!Number.isFinite(minVal)) {
      minVal = 0;
      maxVal = 0;
    }

    return {
      strikes,
      dtes,
      grid,
      minVal,
      maxVal,
      maxAbsGap: Math.max(0.01, maxAbsGap),
    };
  }, [surfaces, view]);

  const hasData = grid.some((row) => row.some((v) => !Number.isNaN(v)));

  if (!hasData) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary">
          {view === 'current'
            ? 'No volatility surface data available'
            : 'No projected volatility data available'}
        </Typography>
      </Box>
    );
  }

  const formatCell = view === 'gap' ? formatGap : formatIv;

  // Backend DTEs count from the data's date; shift them to count from today like the Overview.
  const firstDatedIndex = surfaces.expirationDates.findIndex((d) => d != null);
  const dteOffsetDays =
    firstDatedIndex >= 0
      ? etCalendarDaysUntil(
          dayjs.tz(surfaces.expirationDates[firstDatedIndex], ET),
        ) - Math.round(dtes[firstDatedIndex])
      : 0;
  const dteLabel = (c: number): string =>
    `${Math.round(dtes[c]) + dteOffsetDays}d`;

  const lowValueCell = theme.palette.iVol.fixedStrikeMatrix.lowValueCell;
  // Log scale so a 20%->40% move reads the same as 40%->80%, since real IV surfaces are right-skewed.
  const logMin = Math.max(minVal, MIN_IV);
  const logMax = Math.max(maxVal, logMin + MIN_IV);
  const levelScale = scaleSequentialLog()
    .interpolator(interpolateRgb(negColor, posColor))
    .domain([logMin, logMax])
    .clamp(true);
  const gapNegativeScale = scaleSequential()
    .interpolator(interpolateRgb(negColor, lowValueCell))
    .domain([-maxAbsGap, 0]);
  const gapPositiveScale = scaleSequential()
    .interpolator(interpolateRgb(lowValueCell, posColor))
    .domain([0, maxAbsGap]);

  const cellColor = (v: number): string => {
    if (Number.isNaN(v)) {
      return 'transparent';
    }
    if (view === 'gap') {
      return v < 0 ? gapNegativeScale(v) : gapPositiveScale(v);
    }
    return levelScale(v);
  };

  const cellTextColor = (bg: string): string => {
    const rgb = d3Color(bg)?.rgb();
    if (rgb == null) {
      return theme.palette.text.primary;
    }
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1_000;
    return brightness > 150 ? '#000' : '#fff';
  };

  return (
    <Box sx={{ height: '100%', width: '100%', overflow: 'auto' }}>
      <Box
        role="table"
        aria-label="Implied volatility by strike and days to expiration"
        sx={{
          display: 'grid',
          gridTemplateColumns: `64px repeat(${dtes.length}, minmax(56px, 1fr))`,
          gap: '2px',
          minWidth: 'fit-content',
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 3,
            bgcolor: 'background.paper',
          }}
        />
        {dtes.map((_, c) => {
          const date = surfaces.expirationDates[c];
          return (
            <Box
              key={`h-${c}`}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.paper',
                textAlign: 'center',
                pb: 0.5,
                lineHeight: 1.1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
              }}
            >
              {date != null && (
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dayjs.tz(date, ET).format('MMM D')}
                </Typography>
              )}
              <Typography sx={{ fontSize: '9px', color: 'text.tertiary' }}>
                {dteLabel(c)}
              </Typography>
            </Box>
          );
        })}

        {strikes.map((strike, r) => [
          <Box
            key={`r-${r}`}
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              pr: 0.75,
            }}
          >
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
              {formatStrike(strike)}
            </Typography>
          </Box>,
          ...dtes.map((_, c) => {
            const v = grid[r][c];
            const bg = cellColor(v);
            return (
              <Box
                key={`c-${r}-${c}`}
                title={
                  Number.isNaN(v)
                    ? undefined
                    : `Strike ${formatStrike(strike)} @ ${dteLabel(c)}: ${formatCell(v)}`
                }
                sx={{
                  minHeight: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '2px',
                  backgroundColor: bg,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 500,
                    color: cellTextColor(bg),
                  }}
                >
                  {Number.isNaN(v) ? '' : formatCell(v)}
                </Typography>
              </Box>
            );
          }),
        ])}
      </Box>
    </Box>
  );
};

export default VolatilityHeatmap;
